import { useEffect, useMemo, useState } from 'react'
import {
  OptionsHelper,
  Permission,
  useDebounce,
  validateDateRange,
} from '../../../../../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { CustomSelect, DateInput, ErrorLabel, IOption, Label } from '../../../../../../components'
import { useAuth } from '../../../../../auth'
import { montaFiltroInicial, useQueryRequest } from '../../core/QueryRequestProvider'
import { MAX_DIAS_PERIODO } from '../../core/formatacao'

const ID_EMPRESA = 'indicadores-filtro-empresa'
const ID_DATA_INICIO = 'indicadores-filtro-data-inicio'
const ID_DATA_FIM = 'indicadores-filtro-data-fim'
const ID_UNIDADE = 'indicadores-filtro-unidade'
const ID_PROFISSIONAL = 'indicadores-filtro-profissional'
const ID_ERRO_PERIODO = 'indicadores-filtro-periodo-erro'

/**
 * Filtro único da moldura (período, unidade e profissional), consumido pelas
 * duas seções. `empresa` é obrigatória no contrato: quem não escolhe empresa
 * (ADMIN) tem a do vínculo ativo; quem tem escopo amplo escolhe na lista.
 */
export const IndicadoresSearchComponent = () => {
  const { state, updateState } = useQueryRequest()
  const filtro = state.params ?? montaFiltroInicial()
  const { currentUser } = useAuth()
  const { t } = useT()

  const grupo = currentUser?.grupo?.nome
  const escopoAmplo = grupo === Permission.SuperAdmin || grupo === Permission.SuperMedico

  const empresasDoVinculo = useMemo<IOption<number>[]>(() => {
    const vinculos = currentUser?.vinculos?.filter((vinculo) => vinculo.ativo) ?? []

    return vinculos
      .map((vinculo) => vinculo.empresa)
      .filter((empresa): empresa is NonNullable<typeof empresa> => Boolean(empresa?.id))
      .map((empresa) => ({
        value: Number(empresa.id),
        label: empresa.nomeFantasia ?? empresa.nome ?? String(empresa.id),
      }))
      .filter((opcao, indice, lista) => lista.findIndex((i) => i.value === opcao.value) === indice)
  }, [currentUser])

  const empresaUnica = !escopoAmplo && empresasDoVinculo.length === 1

  const [filters, setFilters] = useState({
    // Sem escopo amplo, a empresa vem do vínculo ativo — o contrato exige o parâmetro.
    empresa: filtro.empresa ?? (empresaUnica ? empresasDoVinculo[0] : undefined),
    unidade: filtro.unidade,
    profissional: filtro.profissional,
    inicio: filtro.dataInicio,
    fim: filtro.dataFim,
  })

  useEffect(() => {
    if (!empresaUnica || filters.empresa) return

    setFilters((prev) => ({ ...prev, empresa: empresasDoVinculo[0] }))
  }, [empresaUnica, empresasDoVinculo, filters.empresa])

  const debouncedFilters = useDebounce(filters, 500)

  const { error: dateError, limits: dateLimits } = useMemo(
    () => validateDateRange(filters.inicio, filters.fim, { maxRangeDays: MAX_DIAS_PERIODO }),
    [filters.inicio, filters.fim]
  )

  // O filtro compartilhado só recebe um período válido; período inválido é
  // mostrado no campo e não gasta requisição, e o período já compartilhado é mantido.
  const { dataInicio: dataInicioAtual, dataFim: dataFimAtual } = filtro
  useEffect(() => {
    const { empresa, unidade, profissional, inicio, fim } = debouncedFilters
    const { error } = validateDateRange(inicio, fim, { maxRangeDays: MAX_DIAS_PERIODO })
    const periodoValido = !error && Boolean(inicio) && Boolean(fim)

    updateState({
      params: {
        empresa,
        unidade,
        profissional,
        dataInicio: periodoValido ? inicio : dataInicioAtual,
        dataFim: periodoValido ? fim : dataFimAtual,
      },
    })
  }, [debouncedFilters, dataInicioAtual, dataFimAtual, updateState])

  const empresaParams = useMemo(() => ({ onlyActive: false }), [])
  const unidadesParams = useMemo(() => ({ empresaId: filters.empresa?.value }), [filters.empresa])

  return (
    <div className='row mb-2' role='group' aria-label={t('indicadores.filtro.titulo')}>
      <div className='col-lg-3 col-md-6 mb-3'>
        <Label className='mb-1' htmlFor={ID_EMPRESA}>
          {t('indicadores.filtro.empresa')}
        </Label>
        <CustomSelect
          inputId={ID_EMPRESA}
          placeholder={t('indicadores.filtro.empresa_placeholder')}
          value={filters.empresa}
          isDisabled={empresaUnica}
          isClearable={!empresaUnica}
          onChange={(empresa: IOption<number> | null) =>
            setFilters((prev) => ({
              ...prev,
              empresa: empresa ?? undefined,
              unidade: undefined,
              profissional: undefined,
            }))
          }
          fetchOptions={escopoAmplo ? OptionsHelper.getOptionsEmpresasAdmin : undefined}
          fetchOptionsParams={escopoAmplo ? empresaParams : undefined}
          options={escopoAmplo ? undefined : empresasDoVinculo}
        />
      </div>
      <div className='col-lg-3 col-md-6 mb-3'>
        <Label className='mb-1' htmlFor={ID_DATA_INICIO}>
          {t('indicadores.filtro.data_inicio')}
        </Label>
        <DateInput
          id={ID_DATA_INICIO}
          value={filters.inicio}
          minDate={dateLimits.startMin}
          maxDate={dateLimits.startMax}
          isInvalid={Boolean(dateError)}
          aria-invalid={Boolean(dateError)}
          aria-describedby={dateError ? ID_ERRO_PERIODO : undefined}
          onChange={(e) => setFilters((prev) => ({ ...prev, inicio: e.target.value }))}
        />
        {dateError && (
          <div className='col-12'>
            <div id={ID_ERRO_PERIODO} role='alert'>
              <ErrorLabel>{dateError}</ErrorLabel>
            </div>
          </div>
        )}
      </div>
      <div className='col-lg-3 col-md-6 mb-3'>
        <Label className='mb-1' htmlFor={ID_DATA_FIM}>
          {t('indicadores.filtro.data_fim')}
        </Label>
        <DateInput
          id={ID_DATA_FIM}
          value={filters.fim}
          minDate={dateLimits.endMin}
          maxDate={dateLimits.endMax}
          isInvalid={Boolean(dateError)}
          aria-invalid={Boolean(dateError)}
          aria-describedby={dateError ? ID_ERRO_PERIODO : undefined}
          onChange={(e) => setFilters((prev) => ({ ...prev, fim: e.target.value }))}
        />
      </div>
      <div className='col-lg-3 col-md-6 mb-3'>
        <Label className='mb-1' htmlFor={ID_UNIDADE}>
          {t('indicadores.filtro.unidade')}
        </Label>
        <CustomSelect
          inputId={ID_UNIDADE}
          placeholder={t('indicadores.filtro.unidade_placeholder')}
          value={filters.unidade}
          isDisabled={!filters.empresa}
          onChange={(unidade: IOption<number> | null) =>
            setFilters((prev) => ({ ...prev, unidade: unidade ?? undefined }))
          }
          fetchOptions={OptionsHelper.getOptionsUnidades}
          fetchOptionsParams={unidadesParams}
        />
      </div>
      {escopoAmplo && (
        <div className='col-lg-3 col-md-6 mb-3'>
          <Label className='mb-1' htmlFor={ID_PROFISSIONAL}>
            {t('indicadores.filtro.profissional')}
          </Label>
          <CustomSelect
            inputId={ID_PROFISSIONAL}
            placeholder={t('indicadores.filtro.profissional_placeholder')}
            value={filters.profissional}
            onChange={(profissional: IOption<number> | null) =>
              setFilters((prev) => ({ ...prev, profissional: profissional ?? undefined }))
            }
            fetchOptions={OptionsHelper.getOptionsProfissionalSaudeAdmin}
          />
        </div>
      )}
    </div>
  )
}
